import React, { useMemo, useState } from 'react';

import { useTheme } from '@material-ui/core/styles';
import { makeMuiStyles, useCommonStyles } from '@styles';
import Color from 'color';
import minBy from 'lodash-es/minBy';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
  },
  colorContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.smallerText,
    height: '1.5rem',
    columnGap: '1rem',
  },
  colorBox: {
    width: '20rem',
    alignSelf: 'stretch',
  },
}));

interface ColorDisplayProps {
  hex: string;
  path: string;
}

const ColorDisplay: React.FC<ColorDisplayProps> = (props) => {
  const { hex, path } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.colorContainer}>
      <div className={classes.colorBox} style={{ backgroundColor: hex }} />

      {Color(hex).hex()}

      <code className={classes.link} onClick={() => navigator.clipboard.writeText(path)}>
        {path}
      </code>
    </div>
  );
};

export const Colors: React.FC = () => {
  const theme = useTheme();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.container}>
      <div className={classes.flexColumn} style={{ flex: 1 }}>
        <ColorDisplay hex={theme.palette.common.black} path="theme.palette.common.black" />
        <ColorDisplay hex={theme.palette.common.white} path="theme.palette.common.white" />
        <ColorDisplay hex={theme.palette.primary.main} path="theme.palette.primary.main" />
        <ColorDisplay hex={theme.palette.primary.light} path="theme.palette.primary.light" />
        <ColorDisplay hex={theme.palette.primary.dark} path="theme.palette.primary.dark" />

        <ColorDisplay hex={theme.palette.secondary.main} path="theme.palette.secondary.main" />
        <ColorDisplay hex={theme.palette.secondary.light} path="theme.palette.secondary.light" />
        <ColorDisplay hex={theme.palette.secondary.dark} path="theme.palette.secondary.dark" />

        <ColorDisplay hex={theme.palette.error.main} path="theme.palette.error.main" />
        <ColorDisplay hex={theme.palette.error.light} path="theme.palette.error.light" />
        <ColorDisplay hex={theme.palette.error.dark} path="theme.palette.error.dark" />

        <ColorDisplay hex={theme.palette.warning.main} path="theme.palette.warning.main" />
        <ColorDisplay hex={theme.palette.warning.light} path="theme.palette.warning.light" />
        <ColorDisplay hex={theme.palette.warning.dark} path="theme.palette.warning.dark" />

        <ColorDisplay hex={theme.palette.info.main} path="theme.palette.info.main" />
        <ColorDisplay hex={theme.palette.info.light} path="theme.palette.info.light" />
        <ColorDisplay hex={theme.palette.info.dark} path="theme.palette.info.dark" />

        <ColorDisplay hex={theme.palette.success.main} path="theme.palette.success.main" />
        <ColorDisplay hex={theme.palette.success.light} path="theme.palette.success.light" />
        <ColorDisplay hex={theme.palette.success.dark} path="theme.palette.success.dark" />
      </div>

      <div className={classes.flexColumn} style={{ flex: 1 }}>
        <ColorDisplay hex={theme.palette.grey[50]} path="theme.palette.grey[50]" />
        <ColorDisplay hex={theme.palette.grey[100]} path="theme.palette.grey[100]" />
        <ColorDisplay hex={theme.palette.grey[200]} path="theme.palette.grey[200]" />
        <ColorDisplay hex={theme.palette.grey[300]} path="theme.palette.grey[300]" />
        <ColorDisplay hex={theme.palette.grey[400]} path="theme.palette.grey[400]" />
        <ColorDisplay hex={theme.palette.grey[500]} path="theme.palette.grey[500]" />
        <ColorDisplay hex={theme.palette.grey[600]} path="theme.palette.grey[600]" />
        <ColorDisplay hex={theme.palette.grey[700]} path="theme.palette.grey[700]" />
        <ColorDisplay hex={theme.palette.grey[800]} path="theme.palette.grey[800]" />
        <ColorDisplay hex={theme.palette.grey[900]} path="theme.palette.grey[900]" />
      </div>
    </div>
  );
};

const FindGrey: React.FC = () => {
  const theme = useTheme();

  const allGrays = [
    [theme.palette.grey[50], 'theme.palette.grey[50]'],
    [theme.palette.grey[100], 'theme.palette.grey[100]'],
    [theme.palette.grey[200], 'theme.palette.grey[200]'],
    [theme.palette.grey[300], 'theme.palette.grey[300]'],
    [theme.palette.grey[400], 'theme.palette.grey[400]'],
    [theme.palette.grey[500], 'theme.palette.grey[500]'],
    [theme.palette.grey[600], 'theme.palette.grey[600]'],
    [theme.palette.grey[700], 'theme.palette.grey[700]'],
    [theme.palette.grey[800], 'theme.palette.grey[800]'],
    [theme.palette.grey[900], 'theme.palette.grey[900]'],
  ];

  const [search, setSearch] = useState('');

  const nearest = useMemo(() => {
    try {
      return minBy(allGrays, ([hex]) => {
        const grey = Color(hex);
        const searched = Color(search.trim());

        return (
          (searched.red() - grey.red()) ** 2 +
          (searched.green() - grey.green()) ** 2 +
          (searched.blue() - grey.blue()) ** 2
        );
      });
    } catch (e) {
      return undefined;
    }
  }, [search]);

  const sanitizedColor = useMemo(() => {
    try {
      return Color(search.trim()).toString();
    } catch (_e) {
      try {
        return Color(`#${search.trim()}`).toString();
      } catch (e) {
        return undefined;
      }
    }
  }, [search]);

  return (
    <div>
      <input
        value={search}
        onChange={(e) => {
          const value = e.target.value;
          try {
            setSearch(Color(value.trim()).hex());
          } catch (_e) {
            try {
              setSearch(Color(`#${value.trim()}`).hex());
            } catch (e) {
              setSearch(value);
            }
          }
        }}
      />
      {sanitizedColor && <ColorDisplay hex={search} path="your color" />}

      {nearest && <ColorDisplay hex={nearest[0]} path={nearest[1]} />}
    </div>
  );
};

export const Theme: React.FC = () => {
  return (
    <div style={{ padding: 10 }}>
      <p>Click to copy</p>
      <Colors />
      <p>Find grey</p>
      <FindGrey />
    </div>
  );
};
